/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from 'react';
import { useBankContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useBank = () => {
    const [state, dispatch] = useBankContext(); // Updated context hook name

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchBanks = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.BANKS, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_BANKS, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const searchBanks = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchBanks();
        },
        [setFilter, setPage, fetchBanks]
    );

    const createBank = useCallback(
        async (bankData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.BANKS, bankData);
                dispatch({ type: ACTIONS.ADD_BANK, payload: data });

                showSuccessMessage('Bank created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateBank = useCallback(
        async (id, bankData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.BANKS}/${id}`, bankData);
                dispatch({ type: ACTIONS.UPDATE_BANK, payload: { id, data } });

                showSuccessMessage('Bank updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteBank = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.BANKS}/${id}`);
                dispatch({ type: ACTIONS.DELETE_BANK, payload: id });

                showSuccessMessage('Bank deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getBankById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.BANKS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_BANK, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listBank = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.BANKS, page, size);
                dispatch({ type: ACTIONS.SET_BANKS, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            banks: state.banks, // Updated to banks
            selectedBank: state.selectedBank, // Updated to selectedBank
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchBanks,
            searchBanks,
            listBank,

            // CRUD Operations
            createBank,
            updateBank,
            deleteBank,
            getBankById,

            // Utilities
            clearError
        }),
        [state, setFilter, setSort, setPage, fetchBanks, searchBanks, createBank, updateBank, deleteBank, getBankById, clearError]
    );
};

export default useBank;
